import { useState, useEffect, useCallback } from 'react';

const td         = () => window.themeData ?? {};
const LS_KEY     = 'anvi_wishlist';
const isLoggedIn = () => !!(td().isLoggedIn);

function wishlistUrl(endpoint = '') {
  const base = (td().apiUrl ?? '').replace(/\/$/, '');
  const path = `/little-anvi/v1/wishlist${endpoint}`;
  if (base.includes('rest_route=') || base.includes('?')) {
    return `${base.replace(/little-anvi.*$/, '')}index.php?rest_route=/little-anvi/v1/wishlist${endpoint}`;
  }
  return `${base}${path}`;
}

const NONCE        = () => td().nonce ?? '';
const JSON_HEADERS = () => ({ 'Content-Type': 'application/json', 'X-WP-Nonce': NONCE() });
const FETCH_OPTS   = () => ({ credentials: 'same-origin', headers: JSON_HEADERS() });

// ── localStorage — single source of truth for instant paint ──────────────────
function lsGet() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]'); } catch { return []; }
}
function lsSet(ids) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(ids)); } catch {}
}

// ── broadcast — keeps every ProductCard in sync ───────────────────────────────
const EVT = 'anvi:wishlist-update';
function broadcast(items) {
  window.dispatchEvent(new CustomEvent(EVT, { detail: { items } }));
}

// ── API helpers ───────────────────────────────────────────────────────────────
async function apiFetch() {
  const res = await fetch(wishlistUrl(), FETCH_OPTS());
  if (!res.ok) throw new Error(`wishlist fetch: ${res.status}`);
  const data = await res.json();
  return data.items ?? [];
}

async function apiToggle(productId) {
  const res = await fetch(wishlistUrl('/toggle'), {
    ...FETCH_OPTS(),
    method: 'POST',
    body:   JSON.stringify({ product_id: productId }),
  });
  if (!res.ok) throw new Error(`wishlist toggle: ${res.status}`);
  const data = await res.json();
  return data.items ?? [];
}

// ── Shared in-memory state ────────────────────────────────────────────────────
let _sharedItems = null;
let _loadPromise = null;

// Always use localStorage first so hearts render instantly on page load.
// For logged-in users the server fetch will correct it in the background.
function getInitial() {
  if (_sharedItems !== null) return _sharedItems;
  return lsGet();
}

export default function useWishlist() {
  const [items, setItems] = useState(getInitial);

  useEffect(() => {
    if (!isLoggedIn()) {
      _sharedItems = lsGet();
      return;
    }

    // Already have server data in memory — just sync state
    if (_sharedItems !== null) {
      setItems(_sharedItems);
      return;
    }

    // Fetch from server once, shared across all hook instances
    if (!_loadPromise) {
      _loadPromise = apiFetch()
        .then((ids) => {
          _sharedItems = ids;
          lsSet(ids);       // keep localStorage in sync with server
          broadcast(ids);
          return ids;
        })
        .catch(() => {
          // Server unreachable / bad auth — fall back to localStorage
          _loadPromise = null;
          const ls = lsGet();
          _sharedItems = ls;
          return ls;
        });
    }
    _loadPromise.then((ids) => setItems(ids));
  }, []);

  // Stay in sync when another card toggles
  useEffect(() => {
    const handler = (e) => setItems(e.detail.items);
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, []);

  const toggle = useCallback(async (productId) => {
    if (!productId) return;
    const id = Number(productId);

    if (!isLoggedIn()) {
      const current = lsGet();
      const updated = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      lsSet(updated);
      _sharedItems = updated;
      setItems(updated);
      broadcast(updated);
      return;
    }

    // Optimistic update — also write to localStorage so it survives page reload
    const prev       = _sharedItems ?? lsGet();
    const optimistic = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    lsSet(optimistic);
    _sharedItems = optimistic;
    setItems(optimistic);
    broadcast(optimistic);

    try {
      const updated = await apiToggle(id);
      lsSet(updated);
      _sharedItems = updated;
      setItems(updated);
      broadcast(updated);
    } catch {
      // Keep optimistic state — don't rollback, so hearts don't flicker off.
      // Next page load will re-sync from the server.
    }
  }, [items]);

  const clearAll = useCallback(async () => {
    if (!(_sharedItems ?? lsGet()).length) return;

    lsSet([]);
    _sharedItems = [];
    setItems([]);
    broadcast([]);

    if (!isLoggedIn()) return;

    try {
      await fetch(wishlistUrl(), { ...FETCH_OPTS(), method: 'DELETE' });
    } catch { /* optimistic clear stands */ }
  }, [items]);

  const isWishlisted = useCallback((productId) => {
    return items.includes(Number(productId));
  }, [items]);

  return { items, toggle, clearAll, isWishlisted, count: items.length };
}
